import React, { useEffect, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen } from 'lucide-react';
import { ModuleId } from '../../types';
import { SubSkillCheatSheet } from './SubSkillCheatSheet';
import { MODULES } from '../../data/modules';
import { useFocusTrap } from '../../hooks/useFocusTrap';
/**
 * RESOLVES: CONFLICT-10 (Hint System Has No Fallback)
 *
 * Wrapper that renders SubSkillCheatSheet with a fallback message
 * when no support content is available for the current family/skill.
 */
function CheatSheetContent({
  moduleId,
  subSkill,
  familyId
}: {
  moduleId: ModuleId;
  subSkill?: string;
  familyId?: string;
}) {
  return <Fragment data-id="element-4275">
      <SubSkillCheatSheet moduleId={moduleId} subSkill={subSkill} familyId={familyId} data-id="element-4276" />
      {/* Fallback message rendered via CSS :empty pseudo-class workaround */}
      <CheatSheetFallback data-id="element-4277" />
    </Fragment>;
}
function CheatSheetFallback() {
  return <div className="cheatsheet-fallback text-center py-8 text-gray-400 hidden last:block" data-id="element-4278">
      <BookOpen className="w-10 h-10 mx-auto mb-3 text-gray-300" data-id="element-4279" />
      <p className="text-sm font-medium text-gray-500" data-id="element-4280">
        Geen extra uitleg beschikbaar
      </p>
      <p className="text-xs text-gray-400 mt-1" data-id="element-4281">
        Voor deze vaardigheid is nog geen spiekbriefje gemaakt. Probeer de
        hint-knop tijdens het oefenen.
      </p>
    </div>;
}
interface CheatSheetDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: ModuleId;
  subSkill?: string;
  familyId?: string;
}
/**
 * Bottom sheet drawer for the Spiekbriefje (cheat sheet / hint system).
 * Slides up from the bottom — feels natural on mobile, less intrusive on desktop.
 */
export function CheatSheetDrawer({
  isOpen,
  onClose,
  moduleId,
  subSkill,
  familyId
}: CheatSheetDrawerProps) {
  const focusTrapRef = useFocusTrap(isOpen);
  // Escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-4282">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-4283" />

          {/* Bottom Sheet */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="cheatsheet-title" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 350
      }} className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[75vh] flex flex-col" data-id="element-4284">
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-1" data-id="element-4285">
              <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-4286" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100" data-id="element-4287">
              <div className="flex items-center gap-2.5" data-id="element-4288">
                <div className="bg-indigo-50 p-1.5 rounded-lg" data-id="element-4289">
                  <BookOpen className="w-5 h-5 text-indigo-600" data-id="element-4290" />
                </div>
                <div data-id="element-4291">
                  <h2 id="cheatsheet-title" className="text-lg font-bold text-slate-900 leading-tight" data-id="element-4292">
                    Spiekbriefje
                  </h2>
                  <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider" data-id="element-4293">
                    {MODULES.find(m => m.id === moduleId)?.title ?? moduleId}
                    {subSkill ? ` · ${subSkill}` : ''}
                  </p>
                </div>
              </div>

              <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600" aria-label="Sluiten" data-id="element-4294">
                <X className="w-5 h-5" data-id="element-4295" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4" data-id="element-4296">
              <CheatSheetContent moduleId={moduleId} subSkill={subSkill} familyId={familyId} data-id="element-4297" />
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}